import {
  SingleItemResponse,
  TransformedSingleItemResponse,
  SingleItemData,
  FilterAttributeValuesType,
  Couplings,
  FilterAttributesApiResponse,
  FilterAttributesType,
  FilterAttribute,
  Attribute,
} from './types';
import { itemMedia } from '../../../../utils/itemMedia';
import { getStaticFilters } from 'utils/getStaticFilters';
import { filterNameAdapter } from 'utils/filterNameAdapter';
import {
  diffPressureMap,
  diffPressureMapMax,
  getDiffPressureFilterAttr,
} from 'utils/diffPressureMap';
import { SelectedFiltersState } from 'app/features/selectedFilters/slice/types';

export const transformSingleItem: (
  dto: TransformedSingleItemResponse,
) => SingleItemData = dto => {
  return {
    brand: dto.brand,
    partNumber: dto.partNumber,
    productId: dto.productId,
    erpPartNumber: dto.erpPartNumber,
    description: dto.description,
    descriptionMap: dto.descriptionMap,
    configuration: dto.configuration,
    image: itemMedia(dto.medias, [
      'product image 01',
      'product image 02',
      'item image',
    ])[0],
    dataSheet: itemMedia(dto.medias, ['product data sheet'])[0],
    logos: itemMedia(dto.medias, 'product icon'),
    price: dto.price,
    exceedMastValue: dto.exceedMastValue,
    stockStatus: dto.stockStatus,
    attributes: dto.attributes,
    quantityMeasure: dto.quantityMeasure,
    ...(dto.couplings && { couplings: dto.couplings }),
    ...(dto.inserts && { inserts: dto.inserts }),
    ...((dto.mountingPart || dto.mountingPart === null) && {
      mountingPart: dto.mountingPart,
    }),
    ...((dto.mountingPlate || dto.mountingPlate === null) && {
      mountingPlate: dto.mountingPlate,
    }),
    ...((dto.pucks || dto.pucks === null) && {
      pucks: dto.pucks,
    }),
    ...((dto.couplings || dto.couplings === null) && {
      couplings: dto.couplings,
    }),
    ...(dto.withStemExtension && {
      withStemExtension: dto.withStemExtension,
    }),
    ...(dto.withSpeedControlBlock && {
      withSpeedControlBlock: dto.withSpeedControlBlock,
    }),
    ...(dto.isGearboxApplicable && {
      isGearboxApplicable: dto.isGearboxApplicable,
    }),
    ...(dto.isNonDirectMount && {
      isNonDirectMount: dto.isNonDirectMount,
    }),
    ...(dto.couplingPartNumber && {
      couplingPartNumber: dto.couplingPartNumber,
    }),
    ...(dto.bracketPartNumber && { bracketPartNumber: dto.bracketPartNumber }),
  };
};

export const transformDTO: (
  items: SingleItemResponse[],
  itemsPrice: any,
  itemsStockStatus: any,
) => any = (
  items: SingleItemResponse[],
  itemsPrice: any,
  itemsStockStatus: any,
) => {
  const transformedItems: SingleItemData[] = items.map(item => {
    let stockStatus = itemsStockStatus.error
      ? null
      : itemsStockStatus.data.items.find(
          elem => elem.number === item.erpPartNumber,
        );
    let price = itemsPrice.error
      ? null
      : itemsPrice.data.items.find(elem => elem.number === item.erpPartNumber);
    return transformSingleItem({
      ...item,
      stockStatus: stockStatus,
      price: price,
    });
  });

  return {
    items: transformedItems,
  };
};

export const transformFilterAttributesDTO = (
  data: FilterAttributesApiResponse[],
  currentStep: number,
  selectFilterAttributes: SelectedFiltersState,
  isGearboxApplicable: boolean,
): FilterAttributesType => {
  const itemBasicForm = selectFilterAttributes[currentStep].filter.basicForm;
  const attributesTransformed = data
    ? getTransformedAttributes(data, itemBasicForm, currentStep)
    : [];

  //Adding static filters
  const staticFilters = getStaticFilters(
    selectFilterAttributes[2].filter.basicForm,
    isGearboxApplicable,
  );
  attributesTransformed.splice(
    attributesTransformed.length,
    0,
    ...staticFilters[currentStep],
  );

  //Adding default selections for radio input type
  const finalResult = attributesTransformed.map(item => {
    let newValues = item.values;
    if (item.attributeIdentifier === 'EF022666__STRING') {
      newValues = [{ value: 'All', label: 'All' }, ...newValues];
    }
    if (item.component === 'radioGroup') {
      newValues = setDefaultValuesforRadio(newValues);
    }
    return { ...item, values: newValues };
  });

  //Sorting filters based on display order.
  const displaySortedResults: FilterAttribute[] = [...finalResult].sort(
    (s1, s2) => s1.displayComponent - s2.displayComponent,
  );
  return { filterAttributes: displaySortedResults };
};

const setDefaultValuesforRadio = (values: Array<FilterAttributeValuesType>) => {
  const val = values.find(valueItem =>
    ['all', 'none', 'no'].includes(valueItem.value.toLowerCase()),
  );
  if (val) {
    return values.map(valItem =>
      valItem.value === val.value ? { ...valItem, default: true } : valItem,
    );
  } else {
    return values.map((valItem, index) =>
      index === 0 ? { ...valItem, default: true } : valItem,
    );
  }
};

//Returns the correct value of the attribute based on the value type
export const getAttributeValue = attributeObject => {
  if (attributeObject.valueType.toLowerCase().includes('string')) {
    return attributeObject.value;
  } else if (attributeObject.valueType.toLowerCase().includes('float')) {
    return attributeObject.valueFloat.toString();
  } else {
    return attributeObject.valueInt.toString();
  }
};

// Filter diff press attribute data and sort it in ascending order
export const getFilterDiffPressAttr = (itemAttr, diffPressureData) => {
  const filterDiffAttr = itemAttr
    .filter(x => diffPressureData.includes(x.attributeIdentifier))
    .sort((a, b) =>
      a.attributeIdentifier.toLowerCase() < b.attributeIdentifier.toLowerCase()
        ? -1
        : b.attributeIdentifier.toLowerCase() >
          a.attributeIdentifier.toLowerCase()
        ? 1
        : 0,
    )
    // eslint-disable-next-line array-callback-return
    .map(x2 => {
      if (x2.valueFloat !== null) {
        return {
          [x2.attributeIdentifier]: x2.valueFloat.toString(),
        };
      }
    });

  return filterDiffAttr;
};

export const getFilteredMandatoryAttr = (
  selectedItem,
  mandatoryItemAttr,
  currentStep,
  actuatorBasicForm,
) => {
  let filterMountingPartAttr: any[] = [];
  const ismountingPart = selectedItem?.mountingPart ? 'Yes' : 'No';
  if (actuatorBasicForm === 'gearbox' && ismountingPart === 'Yes') {
    filterMountingPartAttr = selectedItem.mountingPart.attributes
      .filter(x => mandatoryItemAttr.includes(x.attributeIdentifier))
      .map(x2 => {
        const attrValue = getAttributeValue(x2);
        return { [x2.attributeIdentifier]: attrValue };
      });
    if (filterMountingPartAttr.length === 0) {
      filterMountingPartAttr = [{ EF000040_MMT_FLOAT: '0' }];
    }
    return currentStep === 3
      ? Object.assign({}, ...filterMountingPartAttr, {
          MOUNTING_APPLY: ismountingPart,
          ACTUATOR_TYPE: actuatorBasicForm,
        })
      : Object.assign({}, ...filterMountingPartAttr);
  } else if (
    actuatorBasicForm === 'gearbox' &&
    ismountingPart === 'No' &&
    currentStep === 3
  ) {
    if (filterMountingPartAttr.length === 0) {
      filterMountingPartAttr = [{ EF000040_MMT_FLOAT: '0' }];
    }
    return currentStep === 3
      ? Object.assign({}, ...filterMountingPartAttr, {
          MOUNTING_APPLY: ismountingPart,
          ACTUATOR_TYPE: actuatorBasicForm,
        })
      : Object.assign({}, ...filterMountingPartAttr);
  } else {
    const selectedItemAttributes = selectedItem.attributes || [];
    const filterMandatoryAttr = selectedItemAttributes
      .filter(x => mandatoryItemAttr.includes(x.attributeIdentifier))
      .map(x2 => {
        const attrValue = getAttributeValue(x2);
        return { [x2.attributeIdentifier]: attrValue };
      });
    return currentStep === 3
      ? Object.assign({}, ...filterMandatoryAttr, {
          MOUNTING_APPLY: ismountingPart,
          ACTUATOR_TYPE: actuatorBasicForm,
        })
      : Object.assign({}, ...filterMandatoryAttr);
  }
};

// GET actuator data
export const getActuatorFilter = (
  actuatorAttributes,
  valveAttributes,
  isNonDirectMount,
) => {
  const functionType = actuatorAttributes.filter.attributes.find(
    attrItem =>
      attrItem.attributeIdentifier.toLowerCase() === 'actuatorfunction',
  )?.values[0];
  const supplyPressure =
    actuatorAttributes.filter.attributes.find(
      item =>
        item.attributeIdentifier.toLowerCase() === 'actuatorsupplypressure',
    )?.values[0] ?? '6bar';
  let actuatorFilter;
  if (actuatorAttributes.filter.basicForm === 'pneumatic_actuator') {
    const pneumaticGenericFilter = actuatorAttributes.filter.attributes.filter(
      x =>
        x.attributeIdentifier.toLowerCase() !== 'basicform' &&
        x.attributeIdentifier.toLowerCase() !== 'actuatormediumtype' &&
        x.attributeIdentifier.toLowerCase() !== 'actuatorfunction' &&
        x.attributeIdentifier.toLowerCase() !== 'actuatorsupplypressure' &&
        x.attributeIdentifier.toLowerCase() !== 'differentialpressure',
    );
    actuatorFilter = {
      basicForm: actuatorAttributes.filter.basicForm,
      applyToValve: valveAttributes.filter.basicForm,
      isNonDirectMount: isNonDirectMount,
      function: functionType || 'double_acting',
      supplyPressure: supplyPressure,
      attributes:
        pneumaticGenericFilter.length !== 0 ? [...pneumaticGenericFilter] : [],
    };
  } else {
    const electricGenericFilter = actuatorAttributes.filter.attributes.filter(
      x =>
        x.attributeIdentifier.toLowerCase() !== 'basicform' &&
        x.attributeIdentifier.toLowerCase() !== 'actuatormediumtype' &&
        x.attributeIdentifier.toLowerCase() !== 'differentialpressure',
    );
    actuatorFilter = {
      basicForm: actuatorAttributes.filter.basicForm,
      applyToValve: valveAttributes.filter.basicForm,
      isNonDirectMount: isNonDirectMount,
      attributes:
        electricGenericFilter.length !== 0 ? [...electricGenericFilter] : [],
    };
  }

  return { filter: actuatorFilter };
};

export const getDiffPressureAttr = diffPressureAttribute => {
  const diffPressure = diffPressureAttribute.filter.attributes.find(
    attrItem => attrItem.displayName === 'Differential Pressure',
  );

  const diffPressureData = diffPressure?.id.toString() || 'noData';
  const diffPressureValue = diffPressure?.values[0] || 'noData';

  return {
    [diffPressureData]: diffPressureValue,
  };
};

// Fetch default diff pressure value to be shown in dropdown

export const getAssemblyParamsWithDefaultDiffPressure: (
  selectedItem: SingleItemData,
  differentialPressAttr: Object,
  mandatoryItemAttr: Array<string>,
  inputType: string,
  actuatorBasicForm: string,
  isNonDirectMount?: boolean,
  couplingMandatoryAttr?: Array<string>,
  bracketMandatoryAttr?: Array<string>,
) => any = (
  selectedItem,
  differentialPressAttr,
  mandatoryItemAttr,
  inputType,
  actuatorBasicForm,
  isNonDirectMount = false,
  couplingMandatoryAttr,
  bracketMandatoryAttr,
) => {
  const selectedItemAttributes = selectedItem.attributes || [];
  const filterMandatoryAttr = getFilteredMandatoryAttr(
    selectedItem,
    mandatoryItemAttr,
    2,
    actuatorBasicForm,
  );

  const filterDiffPressAttr =
    actuatorBasicForm !== 'gearbox'
      ? getFilterDiffPressAttr(selectedItemAttributes, differentialPressAttr)
      : [];

  const diffPressureAttribute =
    actuatorBasicForm !== 'gearbox'
      ? filterDiffPressAttr[filterDiffPressAttr.length - 1]
      : [];

  const assemblyParams = {
    attributes: {
      ...filterMandatoryAttr,
      ...diffPressureAttribute,
    },
    ...(isNonDirectMount &&
      actuatorBasicForm !== 'gearbox' &&
      couplingMandatoryAttr &&
      bracketMandatoryAttr &&
      getNonDirectMountAttrs(
        selectedItem,
        couplingMandatoryAttr,
        bracketMandatoryAttr,
      )),
    ...(actuatorBasicForm !== 'gearbox' && {
      inputValues: {
        medium: inputType.toUpperCase(),
      },
    }),
  };

  return { assemblyParams: assemblyParams };
};

// Get diff pressure attr on selection
export const getAssemblyParams: (
  selectedItem: SingleItemData,
  differentialPressAttr: Object,
  mandatoryItemAttr: Array<string>,
  inputType: string,
  actuatorBasicForm: string,
  isNonDirectMount?: boolean,
  couplingMandatoryAttr?: Array<string>,
  bracketMandatoryAttr?: Array<string>,
) => any = (
  selectedItem,
  differentialPressAttr,
  mandatoryItemAttr,
  inputType,
  actuatorBasicForm,
  isNonDirectMount = false,
  couplingMandatoryAttr,
  bracketMandatoryAttr,
) => {
  const filterMandatoryAttr = getFilteredMandatoryAttr(
    selectedItem,
    mandatoryItemAttr,
    2,
    actuatorBasicForm,
  );

  const assemblyParams = {
    attributes: {
      ...filterMandatoryAttr,
      ...differentialPressAttr,
    },
    ...(isNonDirectMount &&
      couplingMandatoryAttr &&
      bracketMandatoryAttr &&
      actuatorBasicForm !== 'gearbox' &&
      getNonDirectMountAttrs(
        selectedItem,
        couplingMandatoryAttr,
        bracketMandatoryAttr,
      )),
    ...(actuatorBasicForm !== 'gearbox' && {
      inputValues: {
        medium: inputType.toUpperCase(),
      },
    }),
  };

  return { assemblyParams: assemblyParams };
};

// Fetch diff pressure attr from the metadata attributes
export const getDiffPressureKeyData = (
  itemAttr: Attribute[] | undefined,
  diffPressureData: Array<string>,
  currentStep: number,
  selectFilterAttributes: SelectedFiltersState,
  filterAttributesData: FilterAttributesApiResponse[],
  isGearboxApplicable: boolean,
): FilterAttributesType => {
  const itemBasicForm = selectFilterAttributes[currentStep].filter.basicForm;
  const actuatorType = selectFilterAttributes[2].filter.basicForm;
  const filterDiffAttr =
    itemAttr &&
    itemAttr
      .filter(
        x =>
          diffPressureData.includes(x.attributeIdentifier) &&
          x.valueFloat !== null &&
          x.attributeIdentifier !== 'EGTF_5664_NU_FLOAT',
      )
      .sort((a, b) =>
        a.attributeIdentifier.toLowerCase() >
        b.attributeIdentifier.toLowerCase()
          ? -1
          : b.attributeIdentifier.toLowerCase() <
            a.attributeIdentifier.toLowerCase()
          ? 1
          : 0,
      )
      // eslint-disable-next-line array-callback-return
      .map((x2, index) => {
        if (x2.valueFloat !== null) {
          if (index === 0) {
            return {
              id: x2.attributeIdentifier,
              label: diffPressureMapMax[x2.attributeIdentifier],
              value: x2.valueFloat,
            };
          } else {
            return {
              id: x2.attributeIdentifier,
              label: diffPressureMap[x2.attributeIdentifier],
              value: x2.valueFloat,
            };
          }
        }
      });

  let attributesTransformed;
  attributesTransformed = getTransformedAttributes(
    filterAttributesData,
    itemBasicForm,
    currentStep,
  );

  const selectedDiffPressAttr = getDiffPressureFilterAttr(filterDiffAttr);
  const staticFilters = getStaticFilters(actuatorType, isGearboxApplicable);
  const result = [
    ...staticFilters[currentStep],
    selectedDiffPressAttr,
    ...attributesTransformed,
  ];

  const filterResult = result.map(filterItem => {
    let newValues = filterItem.values;
    if (filterItem.component === 'radioGroup') {
      newValues = setDefaultValuesforRadio(newValues);
    }
    return { ...filterItem, values: newValues };
  });
  const displaySortedResults = [...filterResult].sort(
    (s1, s2) => s1.displayComponent - s2.displayComponent,
  );

  return {
    filterAttributes: displaySortedResults,
  };
};

// Get medium type for pneumatic and electric actuator
export const getAssemblyParamsInputType = attributes => {
  return attributes.find(
    attrItem =>
      attrItem.attributeIdentifier.toLowerCase() === 'actuatormediumtype',
  )?.values[0];
};

// Fetches unmatched filter attributes
export const checkFilterAttributes = (
  filterAttrTransformed,
  selectedFilters,
  filterAttributesIdentifier,
) => {
  const filteredAttr = selectedFilters.attributes
    .filter(x => filterAttributesIdentifier.includes(x.attributeIdentifier))
    .map(x2 => {
      return { attributeIdentifier: x2.attributeIdentifier, values: x2.values };
    });
  // Transforms filter attribute values to match the format of selected filters
  const filterAttrTransformedValues = filterAttrTransformed.map(x => {
    return {
      attributeIdentifier: x.attributeIdentifier,
      values: x.values.map(x1 => x1.value),
    };
  });
  let updatedFilter;
  filteredAttr.forEach(x1 =>
    filterAttrTransformedValues.forEach(x2 => {
      if (x1.attributeIdentifier === x2.attributeIdentifier) {
        x1.values.forEach(x3 => {
          if (!x2.values.includes(x3)) {
            updatedFilter = {
              attributeIdentifier: x1.attributeIdentifier,
              value: x3,
            };
          }
        });
      }
    }),
  );

  return updatedFilter;
};

export function getTransformedAttributes(
  data: any,
  itemBasicForm: any,
  currentStep: number,
) {
  return data.reduce((acc, x, index) => {
    //Adding additional properties to filter items required for UI
    const filterNames = filterNameAdapter(itemBasicForm);
    let currentAttr = filterNames[currentStep].find(
      y => y.identifier === x.attributeIdentifier,
    );
    if (currentAttr !== undefined) {
      acc[index] = {
        ...x,
        component: currentAttr.type,
        displayComponent: currentAttr.displayOrder,
        displayName: currentAttr.displayName,
        translationId: currentAttr.translationId,
      };
    }
    return acc;
  }, []);
}

export const getNonDirectMountAttrs = (
  selectedItem: SingleItemData,
  couplingMandatoryAttr: Array<string>,
  bracketMandatoryAttr: Array<string>,
) => {
  const couplings = selectedItem?.couplings;
  const couplingsAttr =
    couplings &&
    couplings.map(coupling =>
      getNonDirectMountRequestParams(
        coupling,
        couplingMandatoryAttr,
        bracketMandatoryAttr,
      ),
    );
  return {
    couplings: couplingsAttr,
  };
};

export const getNonDirectMountRequestParams = (
  coupling: Couplings,
  couplingMandatoryAttr: Array<string>,
  bracketMandatoryAttr: Array<string>,
) => {
  const couplingAttrs = getAttributeValuePairObject(
    coupling.attributes,
    couplingMandatoryAttr,
  );
  const bracketAttrs = coupling.brackets.map(bracket => {
    const attrObject = getAttributeValuePairObject(
      bracket.attributes,
      bracketMandatoryAttr,
    );
    return {
      partNumber: bracket.partNumber,
      attributes: Object.assign({}, ...attrObject),
    };
  });
  return {
    partNumber: coupling.partNumber,
    attributes: Object.assign({}, ...couplingAttrs),
    brackets: bracketAttrs,
  };
};

export const getAttributeValuePairObject = (
  attributesList,
  requiredAttributes,
) => {
  return attributesList
    .filter(attr => requiredAttributes.includes(attr.attributeIdentifier))
    .map(selAttr => {
      const attrValue = getAttributeValue(selAttr);
      return { [selAttr.attributeIdentifier]: attrValue };
    });
};
